import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Phone, User, Users, Clock, MessageCircle, CheckCircle, XCircle, Loader2, MessageSquare } from 'lucide-react';
import dayjs from 'dayjs';
import { api, apiErrorMessage } from '../apiClient';
import { toast } from 'sonner';

const STATUS_STYLE = {
  pending: { bg: 'bg-amber-50', fg: 'text-amber-600' },
  contacted: { bg: 'bg-pink-50', fg: 'text-pink-700' },
  closed: { bg: 'bg-emerald-50', fg: 'text-emerald-600' },
};
const ALL_MONTHS = 'all';

export default function OrgEnquiriesPage() {
  const [monthKey, setMonthKey] = useState(ALL_MONTHS);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [updating, setUpdating] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/organizer/enquiries');
      setItems(r.data);
    } catch { setItems([]); }
  }, []);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  // Month pills from data
  const monthOptions = useMemo(() => {
    const seen = new Map();
    for (const e of items) {
      const d = dayjs(e.created_at);
      if (!d.isValid()) continue;
      const key = d.format('YYYY-MM');
      if (!seen.has(key)) seen.set(key, d.format('MMM YYYY'));
    }
    return Array.from(seen.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .slice(0, 12);
  }, [items]);

  const filtered = useMemo(() => {
    if (monthKey === ALL_MONTHS) return items;
    return items.filter((e) => dayjs(e.created_at).format('YYYY-MM') === monthKey);
  }, [items, monthKey]);

  const updateStatus = async (id, status) => {
    setUpdating(true);
    try {
      await api.patch(`/organizer/enquiries/${id}`, { status });
      toast.success(`Marked ${status}`);
      setSelected(null);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setUpdating(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6" data-testid="org-enquiries-page">
      <div className="py-4">
        <h1 className="text-2xl font-black text-gray-900 tracking-tight">Enquiries</h1>
      </div>

      {/* Month filter pills */}
      <div className="flex gap-2 pb-3 overflow-x-auto no-scrollbar" data-testid="org-enq-month-filters">
        <button
          data-testid="org-enq-month-all"
          onClick={() => setMonthKey(ALL_MONTHS)}
          className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-bold border transition-colors ${
            monthKey === ALL_MONTHS
              ? 'bg-brand-magenta border-brand-magenta text-white'
              : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
          }`}
        >
          All
        </button>
        {monthOptions.map(([key, label]) => (
          <button
            key={key}
            data-testid={`org-enq-month-${key}`}
            onClick={() => setMonthKey(key)}
            className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-bold border transition-colors ${
              monthKey === key
                ? 'bg-brand-magenta border-brand-magenta text-white'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-brand-magenta" /></div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20" data-testid="org-enq-empty">
          <MessageSquare className="w-16 h-16 text-gray-300 mb-4" />
          <h3 className="text-lg font-extrabold text-gray-900">No enquiries {monthKey !== ALL_MONTHS ? 'this month' : 'yet'}</h3>
          <p className="text-sm text-gray-500 mt-1 text-center">They'll appear here once customers reach out.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3 pb-8">
          {filtered.map((item) => {
            const st = STATUS_STYLE[item.status] || STATUS_STYLE.pending;
            return (
              <button
                key={item.id}
                data-testid={`org-enq-${item.id}`}
                onClick={() => setSelected(item)}
                className="flex items-center gap-3 bg-white rounded-2xl border border-gray-100 shadow-sm p-3 text-left hover:bg-gray-50 transition-colors"
              >
                <img src={item.event_snapshot?.banner_url} alt="" className="w-[60px] h-[60px] rounded-xl object-cover bg-gray-100 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[15px] font-extrabold text-gray-900 truncate">{item.name}</p>
                  <p className="text-xs text-gray-500 truncate">{item.event_snapshot?.title}</p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className={`${st.bg} ${st.fg} text-[9px] font-black tracking-wide px-2 py-0.5 rounded-full`}>{item.status.toUpperCase()}</span>
                    <a href={`tel:${item.phone}`} onClick={(ev) => ev.stopPropagation()} className="text-[11px] text-brand-magenta font-bold hover:underline">{item.phone}</a>
                    <span className="text-[11px] text-gray-400">{item.guests_count}g</span>
                    {item.notes && <span className="text-[11px] text-gray-400 truncate max-w-[120px]">{item.notes}</span>}
                    <span className="text-[11px] text-gray-400">{dayjs(item.created_at).format('MMM D, h:mm A')}</span>
                  </div>
                </div>
                <a
                  data-testid={`org-enq-call-${item.id}`}
                  href={`tel:${item.phone}`}
                  onClick={(ev) => ev.stopPropagation()}
                  className="w-10 h-10 rounded-full bg-brand-magenta flex items-center justify-center flex-shrink-0 hover:bg-pink-700 transition-colors"
                >
                  <Phone className="w-4 h-4 text-white" />
                </a>
              </button>
            );
          })}
        </div>
      )}

      {/* Detail Sheet */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSelected(null)} />
          <div className="relative bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-lg max-h-[85vh] overflow-y-auto p-6">
            <div className="w-10 h-1 rounded-full bg-gray-300 mx-auto mb-4 sm:hidden" />
            <div className="flex items-center gap-3 bg-gray-50 rounded-2xl p-3 mb-4">
              <img src={selected.event_snapshot?.banner_url} alt="" className="w-[60px] h-[60px] rounded-xl object-cover bg-gray-100" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-extrabold text-gray-900 line-clamp-2">{selected.event_snapshot?.title}</p>
                <p className="text-[11px] text-gray-500 mt-0.5">{selected.event_snapshot?.city} · {dayjs(selected.event_snapshot?.start_datetime).format('MMM D, h:mm A')}</p>
              </div>
            </div>

            <h4 className="text-[11px] font-black text-gray-500 uppercase tracking-wider mb-2">Customer</h4>
            <div className="bg-gray-50 rounded-2xl p-3 flex flex-col gap-2">
              <DetailRow icon={User} label="Name" value={selected.name} />
              <DetailRow icon={Phone} label="Phone" value={selected.phone} />
              <DetailRow icon={Users} label="Guests" value={String(selected.guests_count)} />
              {selected.notes && <DetailRow icon={MessageCircle} label="Notes" value={selected.notes} />}
              <DetailRow icon={Clock} label="Received" value={dayjs(selected.created_at).format('MMM D, YYYY · h:mm A')} />
            </div>

            <h4 className="text-[11px] font-black text-gray-500 uppercase tracking-wider mt-4 mb-2">Actions</h4>
            <a data-testid="sheet-call-customer-btn" href={`tel:${selected.phone}`}
              className="w-full flex items-center justify-center gap-2 bg-brand-magenta hover:bg-pink-700 text-white py-3.5 rounded-2xl font-black text-[15px] transition-colors">
              <Phone className="w-4 h-4" /> Call Customer
            </a>
            <div className="flex gap-2 mt-2">
              <button data-testid="sheet-mark-contacted-btn" disabled={updating || selected.status === 'contacted'}
                onClick={() => updateStatus(selected.id, 'contacted')}
                className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-2xl bg-white border-2 border-gray-200 text-gray-900 font-extrabold text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors">
                <CheckCircle className="w-4 h-4 text-brand-magenta" /> Contacted
              </button>
              <button data-testid="sheet-mark-closed-btn" disabled={updating || selected.status === 'closed'}
                onClick={() => updateStatus(selected.id, 'closed')}
                className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-2xl bg-white border-2 border-gray-200 text-gray-900 font-extrabold text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors">
                <XCircle className="w-4 h-4 text-red-500" /> Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-[11px] text-gray-400 font-bold uppercase tracking-wide">{label}</p>
        <p className="text-sm font-semibold text-gray-900 mt-0.5">{value}</p>
      </div>
    </div>
  );
}
